// Helper xuất Excel có định dạng (title + ngày xuất + header màu + border + banding)
// Dùng xlsx-js-style (SheetJS community + style). Mẫu gốc: promotion.vue / scholarship.vue.
// KHÔNG dùng /utilities/exportexcel cho file cần title/style — json2excel chỉ hỗ trợ header phẳng.
import * as XLSX from "xlsx-js-style";

export interface XlsxColumn {
    label: string;
    width: number; // độ rộng cột (wch)
    align?: "L" | "C" | "R"; // mặc định L
    wrap?: boolean; // wrap text cho cột nội dung dài
    locked?: boolean; // cột THAM CHIẾU/khóa (không nhập ngược) -> tô màu xám khác để dễ nhận biết
    text?: boolean; // ép ĐỊNH DẠNG TEXT (@) cho ô data: Excel không tự đổi ngày/bỏ số 0 đầu (ngày, SĐT, CCCD, ĐTƯT...)
}

const TEAL = "0F766E", HEADER_TEXT = "FFFFFF", TEXT = "0F172A";
const MUTED = "64748B", BORDER = "CBD5E1", BAND = "F1F5F9";
// Cột khóa/tham chiếu: header xám đậm + data xám nhạt (báo "đừng sửa")
const LOCK_HEADER = "475569", LOCK_DATA = "E2E8F0", LOCK_TEXT = "475569";

const pad = (n: number) => String(n).padStart(2, "0");

export const formatMoneyVN = (v: any) => {
    if (v === null || v === undefined || v === "") return "";
    const num = Number(v);
    return Number.isNaN(num) ? String(v) : num.toLocaleString("vi-VN");
};

export interface XlsxSheetSpec {
    title: string;
    summary: string; // vd: "Tổng: 14 chương trình (120 dòng ưu đãi)"
    sheetName: string;
    columns: XlsxColumn[];
    rows: any[][];
    rowGroups?: number[];
}

/**
 * Xuất file .xlsx đã style: title merge ngang, dòng "Ngày xuất … | summary",
 * header nền teal chữ trắng, data border + căn lề theo cột + banding.
 * - rowGroups: index nhóm của từng dòng để tô nền xen kẽ THEO NHÓM (vd: các bậc học kỳ
 *   cùng 1 chương trình chung màu). Bỏ trống -> xen kẽ theo từng dòng.
 */
export function exportStyledXlsx(opts: XlsxSheetSpec & { fileBaseName: string }) {
    const { fileBaseName, ...sheet } = opts;
    exportStyledXlsxWorkbook({ fileBaseName, sheets: [sheet] });
}

/**
 * Port nhanh các export cũ kiểu /utilities/exportexcel (payload {header: {key: label}, items: [{key: value}]})
 * sang file styled — giữ nguyên bộ cột/thứ tự của header map, khỏi viết lại từng trang.
 * widths: override độ rộng (wch) theo key khi cần.
 */
export function exportStyledFromHeaderMap(opts: {
    title: string;
    fileBaseName: string;
    sheetName?: string;
    summary?: string;
    header: Record<string, string>;
    items: any[];
    widths?: Record<string, number>;
}) {
    const keys = Object.keys(opts.header);
    const columns: XlsxColumn[] = keys.map((k) => ({
        label: opts.header[k],
        width: (opts.widths && opts.widths[k]) || Math.max(10, Math.min(32, String(opts.header[k]).length + 6)),
    }));
    const rows = (opts.items || []).map((it: any) => keys.map((k) => (it && it[k] !== null && it[k] !== undefined ? it[k] : "")));
    exportStyledXlsx({
        title: opts.title,
        summary: opts.summary || `Tổng: ${rows.length} dòng`,
        sheetName: opts.sheetName || "Data",
        fileBaseName: opts.fileBaseName,
        columns,
        rows,
    });
}

/** Xuất workbook nhiều sheet, mỗi sheet cùng layout style như exportStyledXlsx. */
export function exportStyledXlsxWorkbook(opts: { fileBaseName: string; sheets: XlsxSheetSpec[] }) {
    const now = new Date();
    const exportedAt = `${pad(now.getDate())}/${pad(now.getMonth() + 1)}/${now.getFullYear()} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const wb = XLSX.utils.book_new();
    for (const sheet of opts.sheets) {
        XLSX.utils.book_append_sheet(wb, buildStyledSheet(sheet, exportedAt), sheet.sheetName);
    }
    const filenameDate = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
    XLSX.writeFile(wb, `${opts.fileBaseName}-${filenameDate}.xlsx`);
}

/** Số dòng mà 1 tiêu đề cột chiếm khi Excel wrap theo TỪ trong cột rộng `width` ký tự. */
function wrappedLineCount(label: string, width: number): number {
    const w = Math.max(4, width || 8);
    let lines = 1, len = 0;
    for (const word of String(label || "").split(/\s+/).filter(Boolean)) {
        const need = len === 0 ? word.length : len + 1 + word.length;
        if (need <= w) { len = need; continue; }
        lines++;
        len = word.length;
        while (len > w) { lines++; len -= w; } // từ dài hơn cột -> Excel cắt giữa từ
    }
    return lines;
}

function buildStyledSheet(spec: XlsxSheetSpec, exportedAt: string) {
    const { title, summary, columns, rows, rowGroups } = spec;
    const colCount = columns.length;

    const aoa: any[][] = [
        [title],
        [`Ngày xuất: ${exportedAt}  |  ${summary}`],
        [],
        columns.map((c) => c.label),
        ...rows,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: colCount - 1 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: colCount - 1 } },
    ];
    ws["!cols"] = columns.map((c) => ({ wch: c.width }));

    const thin = { style: "thin", color: { rgb: BORDER } };
    const borderAll = { top: thin, bottom: thin, left: thin, right: thin };
    const alignOf = (c: number) =>
        columns[c].align === "C" ? "center" : columns[c].align === "R" ? "right" : "left";

    const setStyle = (r: number, c: number, style: any) => {
        const addr = XLSX.utils.encode_cell({ r, c });
        if (!ws[addr]) ws[addr] = { t: "s", v: "" }; // border cần cell tồn tại
        ws[addr].s = style;
    };
    // Title + subtitle (style cả dải merge để nền/border phủ kín)
    for (let c = 0; c < colCount; c++) {
        setStyle(0, c, { font: { sz: 16, bold: true, color: { rgb: TEAL } }, alignment: { horizontal: "center", vertical: "center" } });
        setStyle(1, c, { font: { sz: 10, italic: true, color: { rgb: MUTED } }, alignment: { horizontal: "center", vertical: "center" } });
    }
    // Header (cột khóa/tham chiếu -> nền xám đậm thay vì teal)
    for (let c = 0; c < colCount; c++) {
        setStyle(3, c, {
            font: { sz: 11, bold: true, color: { rgb: HEADER_TEXT } },
            fill: { patternType: "solid", fgColor: { rgb: columns[c].locked ? LOCK_HEADER : TEAL } },
            alignment: { horizontal: "center", vertical: "center", wrapText: true },
            border: borderAll,
        });
    }
    // Data (cột khóa -> nền xám nhạt + chữ xám, đè banding; báo "đừng sửa")
    // numFmt "@" = ĐỊNH DẠNG TEXT: Excel KHÔNG tự chuyển "01/01/2000"->date hay bỏ số 0 đầu (SĐT/CCCD/00),
    // nên đọc lại file (kể cả sau khi mở/lưu bằng Excel) vẫn ĐÚNG chuỗi gốc.
    rows.forEach((_row, i) => {
        const r = 4 + i;
        const banded = (rowGroups ? rowGroups[i] : i) % 2 === 1;
        for (let c = 0; c < colCount; c++) {
            const locked = columns[c].locked;
            setStyle(r, c, {
                font: { sz: 11, color: { rgb: locked ? LOCK_TEXT : TEXT } },
                alignment: { horizontal: alignOf(c), vertical: "center", ...(columns[c].wrap ? { wrapText: true } : {}) },
                border: borderAll,
                ...(columns[c].text ? { numFmt: "@" } : {}),
                ...(locked ? { fill: { patternType: "solid", fgColor: { rgb: LOCK_DATA } } } : (banded ? { fill: { patternType: "solid", fgColor: { rgb: BAND } } } : {})),
            });
        }
    });
    // Chiều cao 4 dòng đầu (data không set height cứng để Excel tự tăng khi wrap).
    // Dòng header cao THEO SỐ DÒNG tiêu đề dài nhất — trước cố định 30pt nên tiêu đề 3 dòng bị cắt.
    const headerLines = columns.reduce((max, c) => Math.max(max, wrappedLineCount(c.label, c.width)), 1);
    ws["!rows"] = [{ hpt: 28 }, { hpt: 16 }, { hpt: 6 }, { hpt: Math.max(30, headerLines * 14 + 8) }];
    ws["!autofilter"] = { ref: XLSX.utils.encode_range({ s: { r: 3, c: 0 }, e: { r: 3 + rows.length, c: colCount - 1 } }) };

    return ws;
}
